import { useState } from "react";
import { fetchJson } from "@/utils/api";
import type { GenerateItemsResponse } from "@/types/api";
import Spinner from "@/components/ui/Spinner";
import { Sparkles, ArrowLeft } from "lucide-react";

interface AiItemGeneratorProps {
  onItemsGenerated: (items: { name: string; description?: string }[]) => void;
  onBack: () => void;
}

export default function AiItemGenerator({
  onItemsGenerated,
  onBack,
}: AiItemGeneratorProps) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canGenerate = prompt.trim().length >= 3 && !loading;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError(null);

    try {
      const data = await fetchJson<GenerateItemsResponse>("/generate-items", {
        method: "POST",
        body: { prompt: prompt.trim() },
      });
      onItemsGenerated(data.items);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to generate items",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to categories
      </button>

      <h2 className="font-display text-xl font-bold text-text">
        Generate with AI
      </h2>
      <p className="mt-1 text-sm text-text-secondary">
        Describe your scavenger hunt theme and AI will create items for you
      </p>

      <div className="mt-4 space-y-3">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value.slice(0, 200))}
          placeholder='e.g. "beach vacation", "birthday party for kids", "nature hike in the mountains"'
          rows={3}
          maxLength={200}
          className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-base text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-border-focus"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-text-secondary">
            {prompt.length}/200
          </span>
        </div>

        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-base font-bold text-white transition-colors hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Spinner className="h-4 w-4" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Generate Items
            </>
          )}
        </button>

        {error && (
          <div className="rounded-xl border border-error/30 bg-error/5 p-3">
            <p className="text-sm text-error">{error}</p>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="mt-2 text-sm font-medium text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
